import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, ReplaySubject } from 'rxjs';

import { enumChargesDistributionType, enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { CodeRepository, CurrenciesRepository, ShipmentsV2Repository } from '@data-layer/repositories';
import { CurrencyRateModel, IncoTermModel, ShipmentModel, ShortModel, ValidationMessageModel } from '@data-layer/models/common';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { BUYER_MISSING, CONSIGNEE_MISSING, SELLER_MISSING } from '@constants/entity-validation-messages';
import { EntrySummaryService } from '@services/entry-summary.service';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import { DateHelper } from '@helpers/date.helper';
import { EsLabelsService, EsStepsService } from '@shared/es-mutual/services';
import {
  blBuyerMissingFields,
  blConsigneeMissingFields,
  blMissingFields,
  blMissingFieldsSpec,
  blSellerMissingFields,
} from '../../const/es-bl-missing-fields';
import { take, takeUntil } from 'rxjs/operators';
import { UtilsHelper } from '@helpers/utils-helper';

@Component({
  selector: 'es-details-bill-of-lading-card',
  templateUrl: './es-details-bill-of-lading-card.component.html',
  styleUrls: ['./es-details-bill-of-lading-card.component.scss'],
})
export class EsDetailsBillOfLadingCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public shipment: ShipmentModel;
  @Input() public entrySummaryId: number;
  @Input() public index: number = 0;
  @Input() public hasSingleExportData: boolean = false;
  @Input() public transportationMode: enumTransportationMode = enumTransportationMode.OCEAN;
  @Input() public canEdit: boolean = false;
  @Input() public isOpenInvoicesOnEdit: boolean = false;
  @Input() public connectedIsfs: ShortModel[] = [];
  @Input() public isRequiredDocument: boolean = true;
  // #13499 — "Bill of Lading #N" only when the ES has several BLs; a single BL is titled plain "Bill of Lading"
  @Input() public isMultiBl: boolean = false;

  @Output() public edit: EventEmitter<number> = new EventEmitter();
  @Output() public editConsignee: EventEmitter<number> = new EventEmitter();
  @Output() public editBuyer: EventEmitter<number> = new EventEmitter();
  @Output() public editSupplier: EventEmitter<number> = new EventEmitter();
  @Output() public editInvoice: EventEmitter<{ shipmentId: number; invoiceId: number }> = new EventEmitter();
  @Output() public removeInvoice: EventEmitter<{ shipmentId: number; invoiceId: number }> = new EventEmitter();
  @Output() public editInvoices: EventEmitter<number> = new EventEmitter();
  @Output() public removeShipment: EventEmitter<number> = new EventEmitter();

  public info: any = {
    title: 'DOCUMENT_TYPE.BILL_OF_LADING',
    emptyImage: true,
    bordered: true,
  };
  public foreignPort: any;
  public blEnteredValue: number;
  public currentLabelsData: { titleMaster: string; titleHouse: string; titleCarrier: string; titleNumber: string };
  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public allValidationMessages: ValidationMessageModel[] = [];
  public shipmentValidationMessages: ValidationMessageModel[] = [];
  public sellerValidationMessages: ValidationMessageModel[] = [];
  public consigneeValidationMessages: ValidationMessageModel[] = [];
  public isSellerMissed: boolean = false;
  public isConsigneeMissed: boolean = false;
  public isBuyerMissed: boolean = false;
  public buyerValidationMessages: ValidationMessageModel[] = [];
  public enumChargesDistributionType: typeof enumChargesDistributionType = enumChargesDistributionType;
  public isOpenInvoices: boolean = false;
  public incoterms: IncoTermModel[] = [];
  public ratesMap: Map<string, CurrencyRateModel> = new Map<string, CurrencyRateModel>([
    [
      DEFAULT_CURRENCY_CODE,
      {
        code: DEFAULT_CURRENCY_CODE,
        rate: 1,
      } as CurrencyRateModel,
    ],
  ]);
  public isShowMoreISFs = true;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private route: Router,
    private shipmentsV2Repository: ShipmentsV2Repository,
    private codeRepository: CodeRepository,
    private currenciesRepository: CurrenciesRepository,
    private entrySummaryService: EntrySummaryService,
    private esLabelsService: EsLabelsService,
    private esStepsService: EsStepsService,
  ) {}

  public ngOnInit(): void {
    if (this.hasSingleExportData) {
      this.getPort();
    }
    this.updateBlEnteredValue();
    this.updateLabelsData();
    this.isOpenInvoices = !!JSON.parse(localStorage.getItem('ES_CARD_IS_OPEN_INVOICES' + this.shipment.id));
  }

  public ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.shipment) {
      this.updateBlEnteredValue();
    }
    if (simpleChanges.hasSingleExportData && this.hasSingleExportData) {
      this.getPort();
    }
    if (simpleChanges.transportationMode) {
      this.updateLabelsData();
    }
    if (simpleChanges.shipment || simpleChanges.transportationMode) {
      this.loadAllValidationMessages();
    }
    if (simpleChanges.isOpenInvoicesOnEdit) {
      this.loadInvoiceData();
    }
    if (simpleChanges.isMultiBl) {
      // FORM_LABEL.REFERENCENUMBER_SHIP = "Bill of Lading #" — the card appends the index right after the title
      this.info = { ...this.info, title: this.isMultiBl ? 'FORM_LABEL.REFERENCENUMBER_SHIP' : 'DOCUMENT_TYPE.BILL_OF_LADING' };
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get isAir(): boolean {
    return this.transportationMode === enumTransportationMode.AIR;
  }

  public get isRail(): boolean {
    return this.transportationMode === enumTransportationMode.RAIL;
  }

  public get isTruck(): boolean {
    return this.transportationMode === enumTransportationMode.TRUCK;
  }

  /** #13499 — invoice ordinals ("#N") are shown only when this BL carries more than one invoice. */
  public get isMultiInvoice(): boolean {
    return !!this.shipment?.invoices && this.shipment.invoices.length > 1;
  }

  public goToEdit() {
    this.edit.emit(this.shipment.id);
  }

  public onDelete() {
    this.removeShipment.emit(this.shipment.id);
  }

  public goToEditConsignee() {
    this.editConsignee.emit(this.shipment.id);
  }

  public goToEditBuyer() {
    this.editBuyer.emit(this.shipment.id);
  }

  public goToEditSupplier() {
    this.editSupplier.emit(this.shipment.id);
  }

  public goToEditInvoice(invoiceId: number) {
    this.editInvoice.emit({ shipmentId: this.shipment.id, invoiceId });
  }

  public onDeleteInvoice(invoiceId: number) {
    this.removeInvoice.emit({ shipmentId: this.shipment.id, invoiceId });
  }

  public goToEditInvoices() {
    this.editInvoices.emit(this.shipment.id);
    if (this.isOpenInvoicesOnEdit) {
      this.isOpenInvoices = !this.isOpenInvoices;
      localStorage.setItem('ES_CARD_IS_OPEN_INVOICES' + this.shipment.id, JSON.stringify(this.isOpenInvoices));
    }
  }

  private getPort() {
    if (
      this.shipment &&
      this.shipment.foreignPortOfLadingId &&
      (!this.foreignPort || this.foreignPort.id !== this.shipment.foreignPortOfLadingId)
    ) {
      this.codeRepository
        .getForeignPort(this.shipment.foreignPortOfLadingId)
        .pipe(take(1))
        .subscribe((port) => {
          this.foreignPort = port;
        });
    }
  }

  private updateBlEnteredValue() {
    this.blEnteredValue = 0;

    this.entrySummaryService
      .getShipmentEnteredValue(this.shipment)
      .pipe(take(1))
      .subscribe((value) => (this.blEnteredValue = value));
  }

  private updateLabelsData() {
    this.currentLabelsData = this.esLabelsService.getLabelsData(this.transportationMode);
  }

  private loadAllValidationMessages() {
    this.allValidationMessages = [];
    this.entrySummaryService
      .getShipmentValidationMessagesErrorData(this.shipment, this.transportationMode)
      .pipe(take(1))
      .subscribe((messages) => {
        this.allValidationMessages = messages;
        this.checkIsMissing();
      });
  }

  private checkIsMissing() {
    this.shipmentValidationMessages = [];

    for (const mess of this.allValidationMessages) {
      if (
        blMissingFields.some((error) => mess.text.endsWith(error)) ||
        (this.hasSingleExportData && blMissingFieldsSpec.some((error) => mess.text.endsWith(error)))
      ) {
        this.shipmentValidationMessages.push(mess);
      }
    }

    this.checkIsMissingSeller();
    this.checkIsMissingConsignee();
    this.checkIsMissingBuyer();
  }

  private checkIsMissingSeller() {
    const allValidationMessages = EntrySummaryValidationHelper.getValidationMessages(this.shipment.seller, this.transportationMode);
    this.isSellerMissed =
      (this.shipment &&
        this.shipment.validationMessages &&
        this.shipment.validationMessages.map((item) => item.text).some((error: string) => error.endsWith(SELLER_MISSING))) ||
      !this.shipment.seller;
    this.sellerValidationMessages = [];

    for (const mess of allValidationMessages) {
      if (blSellerMissingFields.some((error) => mess.text.endsWith(error))) {
        this.sellerValidationMessages.push(mess);
      }
    }
  }

  private checkIsMissingConsignee() {
    const allValidationMessages = EntrySummaryValidationHelper.getValidationMessages(this.shipment.consignee, this.transportationMode);

    this.isConsigneeMissed =
      (this.shipment &&
        this.shipment.validationMessages &&
        this.shipment.validationMessages.map((item) => item.text).some((error: string) => error.endsWith(CONSIGNEE_MISSING))) ||
      !this.shipment.consignee;
    this.consigneeValidationMessages = [];

    for (const mess of allValidationMessages) {
      if (blConsigneeMissingFields.some((error) => mess.text.endsWith(error))) {
        this.consigneeValidationMessages.push(mess);
      }
    }
  }

  private checkIsMissingBuyer() {
    const allValidationMessages = EntrySummaryValidationHelper.getValidationMessages(this.shipment.buyer, this.transportationMode);

    this.isBuyerMissed =
      (this.shipment &&
        this.shipment.validationMessages &&
        this.shipment.validationMessages.map((item) => item.text).some((error: string) => error.endsWith(BUYER_MISSING))) ||
      !this.shipment.buyer;
    this.buyerValidationMessages = [];

    for (const mess of allValidationMessages) {
      if (blBuyerMissingFields.some((error) => mess.text.endsWith(error))) {
        this.buyerValidationMessages.push(mess);
      }
    }
  }

  private loadInvoiceData() {
    const allIncoterms: string[] = this.shipment.invoices
      .reduce((prev, curr) => UtilsHelper.concat<any>(prev, curr.incoTermsCode), [])
      .filter((code) => !!code);

    if (allIncoterms && allIncoterms.length) {
      forkJoin(allIncoterms.map((code) => this.codeRepository.getIncoterm(code)))
        .pipe(takeUntil(this.destroy$))
        .subscribe((incoterms) => {
          this.incoterms = incoterms;
        });
    }

    const allCurrenciesCodes: string[] = this.shipment.invoices
      .reduce((prev, curr) => UtilsHelper.concat<any>(prev, curr.currency), [])
      .filter((code) => code !== DEFAULT_CURRENCY_CODE);

    if (allCurrenciesCodes && allCurrenciesCodes.length) {
      forkJoin(allCurrenciesCodes.map((code) => this.currenciesRepository.getRate(code, this.shipment.exportDate || DateHelper.now)))
        .pipe(takeUntil(this.destroy$))
        .subscribe((currencies: CurrencyRateModel[]) => {
          currencies.filter((currency) => !!currency).forEach((currency) => this.ratesMap.set(currency.code, currency));
        });
    }
  }

  private get isHTSOpen(): boolean {
    return this.esStepsService.isCreatingInvoice;
  }
}
